import { useEffect, useMemo, useRef } from 'react'

import { pdfjs } from 'react-pdf'

import {
  pdfChunkSource,
  primePageTree,
} from '@/components/common/PdfViewerV2/pdfChunks'

import { httpRangeSource } from '@/helpers/rangeSource'

export { PDF_CHUNK_BYTES } from '@/components/common/PdfViewerV2/pdfChunks'

interface PdfRangeFile {
  file: string | { range: InstanceType<typeof pdfjs.PDFDataRangeTransport> }
  fillRest: () => void
}

interface PdfRangeFileOptions {
  url: string
  /** Without it the file cannot be read by range, and pdf.js reads the URL. */
  size: number | undefined
  /** Identifies the file across re-signed URLs; a new key starts a new transport. */
  fileKey: string
  /** A new presigned URL, for when the one in hand has expired. */
  refreshUrl?: () => Promise<string>
  onError: () => void
}

/**
 * A PDF for react-pdf's `file`, read a byte range at a time through closed
 * range GETs — the form a bucket answers without a CORS preflight.
 *
 * Given a bare URL, pdf.js decides whether it may read by range from the
 * response's Accept-Ranges header, which our buckets do not expose; it then
 * downloads the whole file before page one can draw. Handed the ranges
 * directly, it reads the trailer, the page tree and page one's objects, and
 * draws while the rest follows (or, with auto-fetch off, never does).
 *
 * `fillRest` reads the rest of the file behind page one. pdf.js's own
 * auto-fetch asks for the chunks it lacks one at a time, one round trip
 * each; filled ahead of it, it finds them in hand.
 */
export const usePdfRangeFile = ({
  url,
  size,
  fileKey,
  refreshUrl,
  onError,
}: PdfRangeFileOptions): PdfRangeFile => {
  const refreshRef = useRef(refreshUrl)
  refreshRef.current = refreshUrl
  const errorRef = useRef(onError)
  errorRef.current = onError
  const urlRef = useRef(url)
  urlRef.current = url

  const abort = useMemo(
    () => new AbortController(),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [fileKey],
  )
  useEffect(() => () => abort.abort(), [abort])

  return useMemo((): PdfRangeFile => {
    if (!size) return { file: urlRef.current, fillRest: () => undefined }
    const source = pdfChunkSource(
      httpRangeSource({
        url: urlRef.current,
        size,
        signal: abort.signal,
        refreshUrl: () =>
          refreshRef.current?.() ?? Promise.resolve(urlRef.current),
      }),
    )
    // Runs alongside pdf.js's own first reads, which it shares chunks with:
    // what pdf.js is handed meanwhile stays until the primer is done with it.
    const priming = primePageTree(source)
    priming.catch(() => undefined)
    source.holdReleases(priming)
    const range = new pdfjs.PDFDataRangeTransport(size, null)
    range.requestDataRange = (begin: number, end: number) => {
      source.read(begin, end, { release: true }).then(
        (chunk) => range.onDataRange(begin, chunk),
        () => {
          if (!abort.signal.aborted) errorRef.current()
        },
      )
    }
    let filling = false
    const fillRest = () => {
      if (filling) return
      filling = true
      source.fill(abort.signal).catch(() => undefined)
    }
    return { file: { range }, fillRest }
  }, [abort, size])
}
